import { useState } from "react";
import { Badge, Button, Table } from "flowbite-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useForm } from "react-hook-form";
import { AiOutlineLoading } from "react-icons/ai";
import { FaCheck, FaEdit } from "react-icons/fa";
import { MdAdd, MdAdminPanelSettings, MdBlock } from "react-icons/md";
import { IoMdSearch } from "react-icons/io";
import { HiOutlineExclamationCircle } from "react-icons/hi";

import { STAFF } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import Input from "../inputs/Input";
import PhoneInput from "../inputs/PhoneInput";
import SelectInput from "../inputs/SelectInput";
import Modal from "../modals/Modal";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { useApplication } from "../../contexts/ApplicationContext";
import {
  formatDate,
  formatNumber,
  getErrorMessage,
} from "../customers/customerHelpers";

const ROLE_META = {
  ADMIN: {
    label: "Administrador",
    color: "purple",
    hint: "Enxerga e edita toda a base de clientes",
  },
  ACCOUNT_MANAGER: {
    label: "Gerente de contas",
    color: "info",
    hint: "Enxerga apenas os clientes sob sua responsabilidade",
  },
  OPERATOR: {
    label: "Operador",
    color: "warning",
    hint: "Atua como administrador, mas sem Clientes, Minha carteira, Funil, Métricas e Acessos da equipe, sem entrar no app como usuário e, em Pagamentos, sem os totais",
  },
};

const emptyUser = () => ({
  name: "",
  email: "",
  phoneNumber: "",
  password: "",
  role: "ACCOUNT_MANAGER",
  isActive: true,
});

/**
 * Usuários do back-office: nível de acesso e tamanho da carteira de clientes.
 * A carteira em si é definida no cadastro de cada cliente (campo Responsável).
 */
const BackofficeUsersTable = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useApplication();
  const [searchTerm, setSearchTerm] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusError, setStatusError] = useState("");

  const debouncedSearch = useDebouncedValue(searchTerm, 400);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: emptyUser() });

  const { isLoading, data } = useQuery(
    ["backoffice-users", debouncedSearch],
    async () => {
      const { data: response } = await axios.get(
        `${STAFF.LIST}?search=${encodeURIComponent(debouncedSearch)}`
      );

      return response.data;
    }
  );

  const users = data?.data || [];
  const summary = data?.summary || {};

  const saveMutation = useMutation(
    (payload) =>
      editingUser?.id
        ? axios.put(`${STAFF.LIST}/${editingUser.id}`, payload)
        : axios.post(STAFF.LIST, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("backoffice-users");
        queryClient.invalidateQueries("backoffice-user-options");
        setIsFormOpen(false);
        setEditingUser(null);
      },
      onError: (error) => setFormError(getErrorMessage(error)),
    }
  );

  // Ativar/desativar não passa pelo formulário: o back-end tem uma rota só
  // para a situação, então nada mais do cadastro é tocado por acidente.
  const statusMutation = useMutation(
    ({ id, isActive }) =>
      axios.put(`${STAFF.LIST}/${id}/status`, { isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("backoffice-users");
        queryClient.invalidateQueries("backoffice-user-options");
        setStatusTarget(null);
      },
      onError: (error) => setStatusError(getErrorMessage(error)),
    }
  );

  const isCurrentUser = (user) =>
    Number(currentUser?.id) === Number(user.id);

  const openStatusConfirm = (user) => {
    setStatusError("");
    setStatusTarget(user);
  };

  const openForm = (user = null) => {
    setEditingUser(user);
    setFormError("");
    reset(
      user
        ? {
            name: user.name || "",
            email: user.email || "",
            phoneNumber: user.phoneNumber || "",
            password: "",
            role: user.role || "ACCOUNT_MANAGER",
            isActive: Boolean(user.isActive),
          }
        : emptyUser()
    );
    setIsFormOpen(true);
  };

  const onSubmit = (values) => {
    const payload = { ...values };

    // Em edição, senha em branco significa "manter a atual".
    if (editingUser?.id && !payload.password) delete payload.password;

    saveMutation.mutate(payload);
  };

  return (
    <>
      <div className="space-y-4">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Administração
              </p>
              <h2 className="text-xl font-semibold text-slate-900">
                Acessos do back-office
              </h2>
              <p className="text-sm text-slate-500">
                Quem entra no painel, o que cada um enxerga e o tamanho da
                carteira de clientes.
              </p>
            </div>
            <Button size="sm" onClick={() => openForm(null)}>
              <MdAdd className="mr-2 h-4 w-4" />
              Novo acesso
            </Button>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: "Usuários", value: summary.total },
            { label: "Ativos", value: summary.active },
            { label: "Gerentes de contas", value: summary.accountManagers },
            { label: "Clientes sem responsável", value: summary.unassignedClients },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                {card.label}
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {formatNumber(card.value)}
              </p>
            </div>
          ))}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 focus-within:border-pink-500">
            <IoMdSearch className="shrink-0 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por nome ou e-mail"
              className="w-full border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </section>

        <section className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <Table hoverable>
            <Table.Head className="bg-slate-50">
              <Table.HeadCell>Usuário</Table.HeadCell>
              <Table.HeadCell>Nível de acesso</Table.HeadCell>
              <Table.HeadCell className="text-center">Clientes</Table.HeadCell>
              <Table.HeadCell className="hidden md:table-cell">
                Situação
              </Table.HeadCell>
              <Table.HeadCell className="text-center">Ações</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, rowIndex) => (
                  <Table.Row key={`skeleton-${rowIndex}`}>
                    {Array.from({ length: 5 }).map((__, cellIndex) => (
                      <Table.Cell key={cellIndex}>
                        <div className="h-3 w-full animate-pulse rounded-full bg-slate-200" />
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))
              ) : users.length ? (
                users.map((user) => {
                  const roleMeta = ROLE_META[user.role] || {
                    label: user.role,
                    color: "gray",
                  };

                  return (
                    <Table.Row key={user.id} className="bg-white">
                      <Table.Cell>
                        <p className="text-sm font-semibold text-slate-900">
                          {user.name}
                        </p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          color={roleMeta.color}
                          className="w-fit text-xs"
                          title={roleMeta.hint}
                        >
                          {roleMeta.label}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell className="text-center">
                        <span className="text-sm font-semibold text-slate-900">
                          {formatNumber(user.clientCount)}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="hidden md:table-cell">
                        <Badge
                          color={user.isActive ? "success" : "gray"}
                          className="w-fit text-xs"
                        >
                          {user.isActive ? "Ativo" : "Inativo"}
                        </Badge>
                        <span className="mt-1 block text-xs text-slate-500">
                          desde {formatDate(user.createdAt)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => openForm(user)}
                            title="Editar acesso"
                          >
                            <FaEdit />
                          </Button>
                          {/* Desativar o próprio acesso trancaria a pessoa
                              fora do painel, então a ação não aparece. */}
                          {isCurrentUser(user) ? null : (
                            <Button
                              size="xs"
                              color={user.isActive ? "failure" : "success"}
                              onClick={() => openStatusConfirm(user)}
                              title={
                                user.isActive
                                  ? "Desativar acesso"
                                  : "Reativar acesso"
                              }
                            >
                              {user.isActive ? <MdBlock /> : <FaCheck />}
                            </Button>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={5} className="py-10 text-center text-sm text-slate-500">
                    Nenhum usuário encontrado.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </section>
      </div>

      <Modal
        isOpen={Boolean(statusTarget)}
        onClose={() => setStatusTarget(null)}
        size="sm"
        icon={HiOutlineExclamationCircle}
        title={
          statusTarget?.isActive ? "Desativar acesso" : "Reativar acesso"
        }
        subtitle={statusTarget?.name}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              color="light"
              onClick={() => setStatusTarget(null)}
              disabled={statusMutation.isLoading}
            >
              Cancelar
            </Button>
            <Button
              color={statusTarget?.isActive ? "failure" : "success"}
              onClick={() =>
                statusMutation.mutate({
                  id: statusTarget.id,
                  isActive: !statusTarget.isActive,
                })
              }
              isProcessing={statusMutation.isLoading}
              processingSpinner={
                <AiOutlineLoading className="h-5 w-5 animate-spin" />
              }
              disabled={statusMutation.isLoading}
            >
              {statusTarget?.isActive ? "Sim, desativar" : "Sim, reativar"}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-700">
          {statusTarget?.isActive
            ? `${statusTarget?.name} perde o acesso ao painel imediatamente.`
            : `${statusTarget?.name} volta a acessar o painel com o mesmo nível de acesso.`}
        </p>
        {statusTarget?.isActive && statusTarget?.clientCount ? (
          <p className="mt-2 text-xs text-slate-500">
            A carteira de {formatNumber(statusTarget.clientCount)} cliente(s)
            continua vinculada a este usuário — transfira os clientes se
            precisar redistribuir a responsabilidade.
          </p>
        ) : null}

        {statusError ? (
          <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {statusError}
          </p>
        ) : null}
      </Modal>

      <Modal
        isOpen={isFormOpen}
        onClose={setIsFormOpen}
        size="md"
        icon={MdAdminPanelSettings}
        title={editingUser ? "Editar acesso" : "Novo acesso"}
        subtitle={
          editingUser
            ? `${editingUser.name} · ${formatNumber(editingUser.clientCount)} cliente(s)`
            : "Cadastro de usuário do painel"
        }
        closeOnBackdrop={false}
      >
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <Input
            type="text"
            label="Nome *"
            name="name"
            required
            register={register}
            error={errors?.name && "Este campo é obrigatório"}
          />
          <Input
            type="email"
            label="E-mail *"
            name="email"
            required
            register={register}
            error={errors?.email && "Informe um e-mail válido"}
          />
          <PhoneInput label="Telefone" name="phoneNumber" register={register} />
          <Input
            type="password"
            label={
              editingUser
                ? "Senha (deixe em branco para manter)"
                : "Senha * (mínimo 6 caracteres)"
            }
            name="password"
            required={!editingUser}
            register={register}
            error={errors?.password && "Informe uma senha válida"}
          />
          <SelectInput label="Nível de acesso *" name="role" register={register}>
            <option value="ACCOUNT_MANAGER">Gerente de contas</option>
            <option value="OPERATOR">Operador</option>
            <option value="ADMIN">Administrador</option>
          </SelectInput>
          <p className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {ROLE_META[watch("role")]?.hint}
          </p>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500"
              {...register("isActive")}
            />
            Acesso ativo
          </label>

          {formError ? (
            <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {formError}
            </p>
          ) : null}

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              className="flex-1"
              isProcessing={saveMutation.isLoading}
              processingSpinner={<AiOutlineLoading className="h-5 w-5 animate-spin" />}
              disabled={saveMutation.isLoading}
            >
              {editingUser ? "Salvar" : "Criar acesso"}
            </Button>
            <Button color="light" onClick={() => setIsFormOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default BackofficeUsersTable;
